/* StyleOmg Motion Lab v2
   One-shot editorial entrances; nothing stays invisible while scrolling or loading.
   WAAPI animations clean up automatically and leave existing hover, gallery and cart styles alone.
*/
(() => {
  'use strict';
  if (window.__soMotionLabV2) return;
  window.__soMotionLabV2 = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches || !('IntersectionObserver' in window) ||
      !Element.prototype.animate) return;

  const targets = [
    ['.sohh-copy .sohh-over', 'rise'],
    ['.sohh-copy .sohh-title', 'rise'],
    ['.sohh-copy .sohh-lead', 'rise'],
    ['.sohh-copy .sohh-actions', 'rise'],
    ['.sohh-art', 'soft-scale'],
    ['.sohp-head', 'rise'],
    ['.sohp-card', 'soft-scale'],
    ['.sohow-intro', 'rise'],
    ['.sohow-step', 'rise'],
    ['.sohow-foot', 'rise'],
    ['.sosp-visual', 'soft-scale'],
    ['.sosp-copy', 'rise'],
    ['.sofaq-intro', 'rise'],
    ['.sofaq-item', 'rise'],
    ['#product-grid .grid__item', 'rise'],
    ['.collection .card-wrapper', 'rise'],
    ['.styleomg-pdp .so-product-heading', 'rise'],
    ['.styleomg-pdp .so-smart__head', 'rise'],
    ['.styleomg-pdp .so-smart__card', 'rise']
  ];
  const isHome = document.body.classList.contains('so-home-motion');
  const seen = new WeakSet();
  const activeAnimations = new Set();
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const io = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      io.unobserve(entry.target);
      const el = entry.target;
      if (!el.isConnected || reduced.matches) continue;
      const type = el.dataset.soMotion || 'rise';
      if (isHome && entry.boundingClientRect.top < window.innerHeight * 0.5) continue;
      const distance = isHome ? 8 : (isMobile() ? 10 : 17);
      const duration = isHome ? 360 : (isMobile() ? 480 : 620);
      const delay = isHome ? 0 : Number(el.dataset.soMotionDelay || 0);
      const keyframes = isHome
        ? [{transform: 'translate3d(0,' + distance + 'px,0)'}, {transform: 'none'}]
        : type === 'soft-scale'
        ? [{opacity: 0, transform: 'translate3d(0,' + distance + 'px,0) scale(.985)'},
           {opacity: 1, transform: 'none'}]
        : [{opacity: 0, transform: 'translate3d(0,' + distance + 'px,0)'},
           {opacity: 1, transform: 'none'}];
      try {
        const animation = el.animate(keyframes, {
          duration, delay, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'none'
        });
        activeAnimations.add(animation);
        animation.finished.then(() => activeAnimations.delete(animation))
          .catch(() => activeAnimations.delete(animation));
      } catch (error) {
        /* If an animation fails, native visible content is still unaffected. */
      }
    }
  }, {rootMargin: isHome ? '0px 0px 64px 0px' : '0px 0px -6% 0px', threshold: isHome ? 0 : 0.07});

  function install(root) {
    if (reduced.matches) return;
    const scope = root?.querySelectorAll ? root : document;
    let total = 0;
    for (const [selector, type] of targets) {
      const list = scope === document
        ? [...document.querySelectorAll(selector)]
        : [...(scope.matches?.(selector) ? [scope] : []),
           ...scope.querySelectorAll(selector)];
      for (const el of list) {
        if (seen.has(el) || el.closest('dialog, cart-drawer, .drawer, .shopify-payment-button, .scroll-trigger')) continue;
        if (el.classList.contains('scroll-trigger')) continue;
        seen.add(el);
        const rect = el.getBoundingClientRect();
        // Initially visible content stays visible: no flash/rewind after deferred JS loads.
        if (rect.bottom <= 0 || rect.top < (isHome ? window.innerHeight + 64 : window.innerHeight * 0.88)) continue;
        el.dataset.soMotion = type;
        let delay = 0;
        const group = el.closest('.sohp-grid, .sohow-grid, #product-grid, .sofaq-items');
        if (group) {
          const index = Array.prototype.indexOf.call(group.children, el);
          if (index >= 0) delay = (index % 4) * 45;
        } else if (el.matches('.sohh-copy .sohh-title')) delay = 65;
        else if (el.matches('.sohh-copy .sohh-lead')) delay = 110;
        else if (el.matches('.sohh-copy .sohh-actions')) delay = 155;
        el.dataset.soMotionDelay = String(delay);
        io.observe(el);
        if (++total >= 180) return;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => install(document), {once: true});
  } else {
    install(document);
  }
  document.addEventListener('shopify:section:load', event => install(event.target));
  if (typeof reduced.addEventListener === 'function') {
    reduced.addEventListener('change', event => {
      if (event.matches) {
        io.disconnect();
        activeAnimations.forEach(animation => animation.cancel());
        activeAnimations.clear();
      }
    });
  }
})();